import { useEffect, useState } from "react";
import { Row, Col, Badge, Button, Image, Modal } from "react-bootstrap";
import { useAuth } from "../providers/authprovider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFolderOpen } from "@fortawesome/free-regular-svg-icons";
import {
  faArrowLeft,
  faDownload,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

export function BuildingProtocolFilesGrid(props) {
  const {
    buildings: {
      getBuildingProtocolFiles,
    },
  } = useAuth();

  const [folders, setFolders] = useState([]);
  const [viewingFolder, setViewingFolder] = useState(null);

  function selectBuildingProtocolFilesList() {
    getBuildingProtocolFiles(props.buildingId, (response) => {
      if (response.success) {
        const grouped = {};

        response.content.forEach((item) => {
          const { protocol_id, date } = item;

          if (!grouped[protocol_id]) {
            grouped[protocol_id] = {
              protocol_id,
              date: date?.split("T")[0] || "brak daty",
              files: [],
            };
          }

          grouped[protocol_id].files.push({
            ...item,
            filename: getAttachmentLabelById(item.filename),
          });
        });

        const folderArray = Object.values(grouped);
        setFolders(folderArray);
      }
    });
  }

  useEffect(() => {
    selectBuildingProtocolFilesList();
  }, []);

  const handleFolderClick = (folder) => {
    setViewingFolder(folder);
  };

  const handleBackClick = () => {
    setViewingFolder(null);
  };

  const handleImageDeleted = ({ protocolId, imageId }) => {
    setFolders((prevFolders) => {
      const updatedFolders = prevFolders
        .map((folder) => {
          if (folder.protocol_id !== protocolId) {
            return folder;
          }

          return {
            ...folder,
            files: folder.files.filter((file) => file.id !== imageId),
          };
        })
        .filter((folder) => folder.files.length > 0);

      const updatedViewingFolder = updatedFolders.find(
        (folder) => folder.protocol_id === protocolId
      );

      if (!updatedViewingFolder) {
        setViewingFolder(null);
      } else {
        setViewingFolder(updatedViewingFolder);
      }

      return updatedFolders;
    });
  };

  return (
    <Col xs={12} md={12} lg={6} className="mb-3 p-4 border-start border-1">
      {viewingFolder ? (
        <ProtocolImagesGallery
          folder={viewingFolder}
          onBack={handleBackClick}
          onImageDeleted={handleImageDeleted}
        />
      ) : (
        <Row>
          {folders.map((folder, index) => (
            <Col
              key={folder.protocol_id || index}
              xs={6}
              sm={4}
              md={3}
              lg={2}
              className="text-center mb-4"
            >
              <div
                onClick={() => handleFolderClick(folder)}
                style={{
                  position: "relative",
                  cursor: "pointer",
                  display: "inline-block",
                }}
              >
                <FontAwesomeIcon icon={faFolderOpen} size="2x" />
                <Badge
                  bg="danger"
                  pill
                  style={{
                    position: "absolute",
                    top: "-8px",
                    right: "-12px",
                    fontSize: "0.75rem",
                  }}
                >
                  {folder.files.length}
                </Badge>
              </div>
              <div className="mt-2">{folder.date}</div>
            </Col>
          ))}
        </Row>
      )}
    </Col>
  );
}

export function ProtocolImagesGallery({ folder, onBack, onImageDeleted }) {
  const [images, setImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [hoveredImageId, setHoveredImageId] = useState(null);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    buildings: {
      downloadEventImage: downloadBuildingImage,
      deleteBuildingImage: deleteBuildingProtocolFile,
    },
  } = useAuth();

  useEffect(() => {
    let localImages = [];

    const loadImages = async () => {
      const loadedImages = await Promise.all(
        folder.files.map(
          (file) =>
            new Promise((resolve) => {
              downloadBuildingImage(file.id, ({ success, content }) => {
                if (!success) {
                  return resolve({ ...file, blobUrl: null });
                }

                const blobUrl = URL.createObjectURL(new Blob([content]));
                resolve({ ...file, blobUrl });
              });
            })
        )
      );

      localImages = loadedImages;
      setImages(loadedImages);
    };

    loadImages();

    return () => {
      localImages.forEach((img) => {
        if (img?.blobUrl) {
          URL.revokeObjectURL(img.blobUrl);
        }
      });
    };
  }, [folder, downloadBuildingImage]);

  const selectedImage =
    selectedImageIndex !== null ? images[selectedImageIndex] : null;

  const handleDownload = (file) => {
    if (!file) return;

    downloadBuildingImage(file.id, ({ success, content }) => {
      if (!success) return;

      const url = window.URL.createObjectURL(new Blob([content], { type: "image/jpeg" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${file.filename}.jpg`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    });
  };

  const openDeleteModal = (file, e) => {
    if (e) {
      e.stopPropagation();
    }

    setImageToDelete(file);
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setImageToDelete(null);
  };

  const showPrevImage = () => {
    if (!images.length || selectedImageIndex === null) return;

    setSelectedImageIndex((prev) => {
      if (prev === 0) return images.length - 1;
      return prev - 1;
    });
  };

  const showNextImage = () => {
    if (!images.length || selectedImageIndex === null) return;

    setSelectedImageIndex((prev) => {
      if (prev === images.length - 1) return 0;
      return prev + 1;
    });
  };

  const handleDeleteImage = () => {
    if (!imageToDelete || isDeleting) return;

    setIsDeleting(true);

    deleteBuildingProtocolFile(imageToDelete.id, (response) => {
      setIsDeleting(false);

      if (!response?.success) {
        return;
      }

      if (imageToDelete.blobUrl) {
        URL.revokeObjectURL(imageToDelete.blobUrl);
      }

      const deletedImageId = imageToDelete.id;
      const deletedProtocolId = folder.protocol_id;

      const deletedIndex = images.findIndex((img) => img.id === deletedImageId);
      const nextImages = images.filter((img) => img.id !== deletedImageId);

      setImages(nextImages);

      if (selectedImage?.id === deletedImageId) {
        if (nextImages.length === 0) {
          setSelectedImageIndex(null);
        } else if (deletedIndex >= nextImages.length) {
          setSelectedImageIndex(nextImages.length - 1);
        } else {
          setSelectedImageIndex(deletedIndex);
        }
      } else if (
        selectedImageIndex !== null &&
        deletedIndex !== -1 &&
        deletedIndex < selectedImageIndex
      ) {
        setSelectedImageIndex((prev) => prev - 1);
      }

      onImageDeleted?.({
        protocolId: deletedProtocolId,
        imageId: deletedImageId,
      });

      setImageToDelete(null);
    });
  };

  return (
    <>
      <Button variant="link" className="mb-3" onClick={onBack}>
        <FontAwesomeIcon icon={faArrowLeft} /> lista folderów
      </Button>

      <h6 className="mb-3">
        Wysłane obrazy do protokołu z dnia {folder.date}
      </h6>

      <Row>
        {images.map((file, index) => (
          <Col
            key={file.id || index}
            xs={6}
            sm={6}
            md={4}
            lg={3}
            className="text-center mb-4"
          >
            <div
              onClick={() => setSelectedImageIndex(index)}
              onMouseEnter={() => setHoveredImageId(file.id)}
              onMouseLeave={() => setHoveredImageId(null)}
              style={{
                cursor: "pointer",
                position: "relative",
                display: "inline-block",
                width: "100%",
              }}
            >
              <Button
                variant="danger"
                size="sm"
                onClick={(e) => openDeleteModal(file, e)}
                style={{
                  position: "absolute",
                  top: "8px",
                  right: "8px",
                  zIndex: 2,
                  opacity: hoveredImageId === file.id ? 1 : 0,
                  pointerEvents: hoveredImageId === file.id ? "auto" : "none",
                  transition: "opacity 0.2s ease",
                  borderRadius: "50%",
                  width: "32px",
                  height: "32px",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FontAwesomeIcon icon={faTrash} />
              </Button>

              <Image
                src={file.blobUrl || "/placeholder.png"}
                thumbnail
                style={{
                  maxHeight: "100px",
                  width: "100%",
                  objectFit: "cover",
                }}
                alt={file.filename}
              />

              <div className="mt-1" style={{ fontSize: "0.8rem" }}>
                {file.filename}
              </div>
            </div>
          </Col>
        ))}
      </Row>

      <Modal
        show={selectedImageIndex !== null}
        onHide={() => setSelectedImageIndex(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedImage?.filename}
            {images.length > 1 && selectedImageIndex !== null
              ? ` (${selectedImageIndex + 1}/${images.length})`
              : ""}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="text-center">
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "400px",
            }}
          >
            {images.length > 1 && (
              <Button
                variant="dark"
                onClick={showPrevImage}
                style={{
                  position: "absolute",
                  left: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  borderRadius: "50%",
                  width: "42px",
                  height: "42px",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: 0.85,
                }}
              >
                <span style={{ fontSize: "22px", lineHeight: 1 }}>‹</span>
              </Button>
            )}

            <Image
              src={selectedImage?.blobUrl}
              fluid
              alt={selectedImage?.filename}
              style={{
                maxHeight: "70vh",
                objectFit: "contain",
              }}
            />

            {images.length > 1 && (
              <Button
                variant="dark"
                onClick={showNextImage}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  borderRadius: "50%",
                  width: "42px",
                  height: "42px",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: 0.85,
                }}
              >
                <span style={{ fontSize: "22px", lineHeight: 1 }}>›</span>
              </Button>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="danger"
            onClick={() => openDeleteModal(selectedImage)}
            disabled={!selectedImage}
          >
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            Usuń
          </Button>

          <Button
            variant="primary"
            onClick={() => handleDownload(selectedImage)}
            disabled={!selectedImage}
          >
            <FontAwesomeIcon icon={faDownload} className="me-2" />
            Pobierz
          </Button>

          <Button variant="secondary" onClick={() => setSelectedImageIndex(null)}>
            Zamknij
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!imageToDelete} onHide={closeDeleteModal} centered>
        <Modal.Header closeButton={!isDeleting}>
          <Modal.Title>Potwierdzenie usunięcia</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          Czy na pewno chcesz usunąć zdjęcie
          {imageToDelete?.filename ? ` "${imageToDelete.filename}"` : ""}?
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeDeleteModal}
            disabled={isDeleting}
          >
            Anuluj
          </Button>

          <Button
            variant="danger"
            onClick={handleDeleteImage}
            disabled={isDeleting}
          >
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            {isDeleting ? "Usuwanie..." : "Usuń"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}


const attachmentLabels = [
  {
    label: "Zdjęcie detektor obudowa zewnętrzna/tabliczka",
    id: "detector_enclosure",
  },
  { label: "Zdjęcie detektor głowica", id: "detector_head" },
  {
    label: "Zdjęcie rozdzielnica tabliczka znamionowa",
    id: "distribution_board_nameplate",
  },
  {
    label: "Zdjęcie rozdzielnica elewacja",
    id: "distribution_board_elevation",
  },
  {
    label: "Zdjęcie rozdzielnica wnętrze urządzenia",
    id: "distribution_board_interior",
  },
  { label: "Inne", id: "other" },
];

function getAttachmentLabelById(id) {
  const match = attachmentLabels.find((item) => item.id === id);
  return match ? match.label : id;
}
